import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
  Row,
  Col,
  Button,
  theme,
  Modal,
  AutoComplete,
  Card,
  App as AntdApp,
  notification as antdNotification,
  Space,
  Popover,
  Flex,
  Image,
} from 'antd'
import demopngmaterials from '$assets/img/demoXLSmaterials.png'
import demoxlsmaterials from '$assets/demomaterials.xlsx'
import {
  DownloadOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Tooltip from '$components/Tooltip'

import ClientMaterials from './ClientMaterials'
import DwhMaterialsTable from './DwhMaterialsTable'
import MaterialInfo from './MaterialInfo'
import { fetchAllMaterials as fetchDwhMaterialsAPI } from '$api/evoAPIs'
import {
  createClientMaterial,
  fetchAutocompleteClientMaterials,
  fetchClientMaterials,
  getLastVisitTimestamp,
  mapDwhMaterialsToClientMaterial,
  trackNewDwhMaterialsForUser,
  updateLastVisitTimestamp,
  // invalidateFcByMaterial,
} from '$api/client'
import UploadExcelButton from '../UploadExcelButton'

const normalizeName = (name) => {
  if (!name || typeof name !== 'string') return ''
  return name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

const { useToken } = theme

const MaterialMapper = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { notification } = AntdApp.useApp()
  const { token } = useToken()

  const [highlightedMaterialId, setHighlightedMaterialId] = useState(null)
  const [selectedClientMaterial, setSelectedClientMaterial] = useState(null)

  const [selectedDwhMaterialIds, setSelectedDwhMaterialIds] = useState(
    new Set(),
  )

  const [dwhMaterialClickedForInfo, setDwhMaterialClickedForInfo] =
    useState(null)

  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [newMaterialName, setNewMaterialName] = useState('')
  const [autocompleteOptions, setAutocompleteOptions] = useState([])
  const [listActionTrigger, setListActionTrigger] = useState(0)

  //fetch last visited timestamp
  const { data: lastVisitTimestamp } = useQuery({
    queryKey: ['userLastVisitMaterialPage'],
    queryFn: getLastVisitTimestamp,
  })

  // const invalidateFcMutation = useMutation({
  //   mutationFn: invalidateFcByMaterial,
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ['parts'] })
  //     queryClient.invalidateQueries({ queryKey: ['allparts'] })
  //   },
  //   onError: (error) => {
  //     console.error('Failed to invalidate FC status:', error)
  //     notification.error({ message: t('error_invalidating_fc') })
  //   },
  // })

  const info = (
    <div>
      <p
        style={{
          maxWidth: '200px',
        }}
      >
        {t('parts_master_data.upload.instructions')}
      </p>
      <Image width={200} src={demopngmaterials} />
    </div>
  )

  const downinfo = (
    <div>
      <a href={demoxlsmaterials} download>
        <Button type="dashed">{t('my_materials.example_download')}</Button>
      </a>
    </div>
  )

  const { data: dwhMaterialsPageData, isLoading: isLoadingDwhMaterials } =
    useQuery(
      ['dwhMaterialsListForMapperPage'],
      async () => {
        let allMaterials = []
        let currentPage = 1
        let hasMorePages = true

        while (hasMorePages) {
          const pageData = await fetchDwhMaterialsAPI({
            queryKey: [
              'dwhMaterialsListForMapperPage',
              { page: currentPage, size: 100 },
            ],
          })

          if (pageData.items && pageData.items.length > 0) {
            allMaterials = [...allMaterials, ...pageData.items]

            const totalFetched = currentPage * 100
            hasMorePages = totalFetched < pageData.total
            currentPage++
          } else {
            hasMorePages = false
          }
        }

        const firstPageData = await fetchDwhMaterialsAPI({
          queryKey: ['dwhMaterialsListForMapperPage', { page: 1, size: 100 }],
        })

        return {
          ...firstPageData,
          items: allMaterials,
          size: allMaterials.length,
        }
      },
      {
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
    )

  const dwhMaterialNameMap = useMemo(() => {
    if (!dwhMaterialsPageData || !dwhMaterialsPageData.items) return {}
    const nameMap = dwhMaterialsPageData.items.reduce((acc, material) => {
      acc[material.id.toString()] = material.name
      return acc
    }, {})
    return nameMap
  }, [dwhMaterialsPageData])

  const {
    data: allClientMaterialsForCheck,
    refetch: refetchAllClientMaterials,
  } = useQuery(
    ['allClientMaterialsForCheck'],
    () =>
      fetchClientMaterials({
        queryKey: [
          'allClientMaterialsForCheck',
          { page: 1, size: 100, sort_by: 'name' },
        ],
      }),
    { enabled: false, staleTime: 5 * 60 * 1000 },
  )

  //create array of ids of dwh materials added after that last visit
  const dwhMaterialsIds =
    dwhMaterialsPageData &&
    lastVisitTimestamp &&
    dwhMaterialsPageData.items
      .filter((item) => {
        return (
          new Date(item.created_at) >
          new Date(
            dayjs(lastVisitTimestamp.last_visited_at ?? new Date())
              .subtract(30, 'day')
              .format('YYYY-MM-DDTHH:mm:ssZ'),
          )
        )
      })
      .map((item) => item.id)

  //below query to get materials which are less than 30 days old
  const { data: dwhMaterialTrackData } = useQuery({
    queryKey: ['userDwhMaterialTrack', dwhMaterialsIds],
    queryFn: () => trackNewDwhMaterialsForUser(dwhMaterialsIds),
    enabled: dwhMaterialsIds && dwhMaterialsIds.length > 0,
  })

  //update visit timestamp when opening mymaterials page
  const { mutate } = useMutation({
    mutationFn: () => updateLastVisitTimestamp(),
  })

  useEffect(() => {
    mutate()
  }, [mutate])

  const isDwhMaterialNew = (material_id) => {
    if (dwhMaterialTrackData) {
      return dwhMaterialTrackData.new_item_ids.includes(material_id)
    }
    return false
  }

  const addMaterialMutation = useMutation(createClientMaterial, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsList'] })
      queryClient.invalidateQueries({
        queryKey: ['allClientMaterialsForCheck'],
      })
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsForKPIs'] })
      notification.success({
        message: t('material_added_successfully', { name: data.name }),
      })
      setHighlightedMaterialId(data.id)
      setListActionTrigger((prev) => prev + 1)
      setIsAddModalVisible(false)
      setNewMaterialName('')
      setAutocompleteOptions([])
    },
    onError: (error) => {
      if (error.response && error.response.status === 409) {
        const existingId = error.response.headers['x-existing-id']
        notification.warning({
          message: t('material_already_exists_jumping', {
            name: newMaterialName,
          }),
        })
        setHighlightedMaterialId(existingId)
        setListActionTrigger((prev) => prev + 1)
        setIsAddModalVisible(false)
        setNewMaterialName('')
        setAutocompleteOptions([])
      } else {
        notification.error({ message: t('error_adding_material') })
      }
    },
  })

  const mapMaterialsApiMutation = useMutation(mapDwhMaterialsToClientMaterial, {
    onSuccess: (updatedClientMaterial) => {
      notification.success({ message: t('mapping_successful_message') })

      const newMappedIds = (
        updatedClientMaterial.mapped_dwh_material_ids || []
      ).map((id) => id.toString())
      const newMappedIdsSet = new Set(newMappedIds)
      setSelectedDwhMaterialIds(newMappedIdsSet)

      // DON'T call invalidateFcMutation - the backend handles FC status properly now
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsList'] })
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsForKPIs'] })
      queryClient.invalidateQueries({ queryKey: ['parts'] })
      queryClient.invalidateQueries({ queryKey: ['allparts'] })
      queryClient.invalidateQueries({ queryKey: ['partKpis'] })
      setListActionTrigger((prev) => prev + 1)
    },
    onError: (_error) => {
      notification.error({ message: t('mapping_failed_message') })
    },
  })

  const handleClientMaterialSelect = (_materialId, materialObject) => {
    setSelectedClientMaterial(materialObject) // Store the full object
    let existingMappings = new Set()
    if (materialObject?.mapped_dwh_material_ids) {
      const mappings = Array.isArray(materialObject.mapped_dwh_material_ids)
        ? materialObject.mapped_dwh_material_ids
        : []
      existingMappings = new Set(mappings.map((id) => id.toString()))
    }
    setSelectedDwhMaterialIds(existingMappings)
    setDwhMaterialClickedForInfo(null)
  }

  const handleDwhMaterialSelectionChange = (newSelectedIdsSet) => {
    setSelectedDwhMaterialIds(newSelectedIdsSet)
  }

  const handleMapClick = () => {
    if (!selectedClientMaterial) {
      notification.warning({ message: t('select_client_material_first') })
      return
    }
    mapMaterialsApiMutation.mutate({
      clientMaterialId: selectedClientMaterial.id,
      dwhMaterialIds: selectedDwhMaterialIds,
    })
  }

  const handleUnmapClick = () => {
    if (!selectedClientMaterial) return

    const originalMappedIds = new Set(
      (selectedClientMaterial.mapped_dwh_material_ids || []).map(String),
    )

    const idsToUnmap = selectedDwhMaterialIds
    idsToUnmap.forEach((id) => originalMappedIds.delete(id))

    mapMaterialsApiMutation.mutate({
      clientMaterialId: selectedClientMaterial.id,
      dwhMaterialIds: originalMappedIds,
    })
  }

  const showUnmapButton = useMemo(() => {
    if (!selectedClientMaterial || selectedDwhMaterialIds.size === 0) {
      return false
    }
    const originalMappedIds = new Set(
      (selectedClientMaterial.mapped_dwh_material_ids || []).map(String),
    )
    for (const id of selectedDwhMaterialIds) {
      if (originalMappedIds.has(id)) {
        return true
      }
    }
    return false
  }, [selectedClientMaterial, selectedDwhMaterialIds])

  const mapButtonIsDisabled = !selectedClientMaterial
  const mapButtonText =
    selectedDwhMaterialIds.size > 0
      ? `${t('map_material_button')} (${selectedDwhMaterialIds.size})`
      : t('map_material_button')

  const handleDwhMaterialRowClickForInfo = (dwhMaterialRecord) => {
    setDwhMaterialClickedForInfo(dwhMaterialRecord)
  }

  const handleOpenAddModal = () => {
    refetchAllClientMaterials()
    setIsAddModalVisible(true)
  }

  const handleModalCancel = () => {
    setIsAddModalVisible(false)
    setNewMaterialName('')
    setAutocompleteOptions([])
  }

  const handleModalOk = () => {
    if (!newMaterialName.trim()) {
      notification.error({ message: t('material_name_required') })
      return
    }
    addMaterialMutation.mutate({ name: newMaterialName })
  }

  const handleAutocompleteSearch = async (searchText) => {
    if (searchText && searchText.length >= 2) {
      try {
        const results = await fetchAutocompleteClientMaterials(searchText)
        setAutocompleteOptions(results.map((name) => ({ value: name })))
      } catch (_error) {
        antdNotification.error({ message: t('autocomplete_failed_message') })
        setAutocompleteOptions([])
      }
    } else {
      setAutocompleteOptions([])
    }
  }

  const onAutocompleteSelect = (value) => {
    setNewMaterialName(value)
    const normalizedSelectedName = normalizeName(value)
    const existingMaterial = allClientMaterialsForCheck?.items.find(
      (item) => normalizeName(item.name) === normalizedSelectedName,
    )
    if (existingMaterial) {
      notification.info({
        message: t('material_selected_exists', { name: value }),
      })
      setHighlightedMaterialId(existingMaterial.id)
      setListActionTrigger((prev) => prev + 1)
      setIsAddModalVisible(false)
      setNewMaterialName('')
      setAutocompleteOptions([])
    }
  }

  return (
    <div style={{ padding: '24px 0' }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{t('client_materials_header')}</span>
                <Flex justify="flex-end" vertical id="importMaterialsContainer">
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <UploadExcelButton id="upload_excel_btn_my_materials" />
                    <Button
                      id="upload_button_material"
                      icon={<PlusOutlined />}
                      onClick={handleOpenAddModal}
                      size="medium"
                      style={{
                        borderRadius: '0 10px 10px 0',
                        borderColor: token.colorPrimary,
                        color: token.colorPrimary,
                      }}
                    >
                      {t('add_material_button')}
                    </Button>
                  </div>
                  <div id="xlsxinfo">
                    <Space>
                      {t('my_materials.excel_file')}
                      <div style={{ cursor: 'help' }}>
                        <Popover
                          placement="bottomRight"
                          content={info}
                          title={t('my_materials.data_format')}
                        >
                          <InfoCircleOutlined />
                        </Popover>
                      </div>
                      <Popover
                        placement="bottomRight"
                        content={downinfo}
                        title={t('Download')}
                      >
                        <a href={demoxlsmaterials} download>
                          <DownloadOutlined />
                        </a>
                      </Popover>
                    </Space>
                  </div>
                </Flex>
              </div>
            }
            style={{ height: '100%' }}
          >
            <ClientMaterials
              onMaterialSelect={handleClientMaterialSelect}
              selectedMaterialIdForMapping={selectedClientMaterial?.id}
              dwhMaterialNameMap={dwhMaterialNameMap}
              highlightedMaterialId={highlightedMaterialId}
              onHighlightComplete={() => setHighlightedMaterialId(null)}
              listActionTrigger={listActionTrigger}
              selectedDwhMaterialIds={selectedDwhMaterialIds}
            />
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Row gutter={[16, 16]} style={{ height: '100%' }}>
            <Col xs={24} xl={16}>
              <Card
                title={t('dwh_materials_header')}
                style={{ height: '100%' }}
              >
                <DwhMaterialsTable
                  onSelectionChange={handleDwhMaterialSelectionChange}
                  selectedDwhKeysFromParent={selectedDwhMaterialIds}
                  dwhMaterialsData={dwhMaterialsPageData}
                  isDwhMaterialNew={isDwhMaterialNew}
                  isLoadingDwhMaterials={isLoadingDwhMaterials}
                  onRowClick={handleDwhMaterialRowClickForInfo}
                />
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card
                title={
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    {t('material_info_title')}
                  </span>
                }
                style={{
                  height: '500px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                bodyStyle={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 0,
                }}
              >
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                  <MaterialInfo
                    selectedDwhMaterialForInfo={dwhMaterialClickedForInfo}
                    selectedDWHMaterialIds={selectedDwhMaterialIds}
                    dwhMaterialsData={dwhMaterialsPageData?.items || []}
                  />
                </div>
                <div
                  style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Button
                    id="material_mapper_button"
                    type="primary"
                    onClick={handleMapClick}
                    disabled={mapButtonIsDisabled}
                    style={{ width: '100%' }}
                    size="medium"
                  >
                    {mapButtonText}
                  </Button>
                  {showUnmapButton && (
                    <Tooltip
                      title={t(
                        'unmap_tooltip',
                        t('my_materials.material_mapper_toolip'),
                      )}
                    >
                      <Button
                        type="primary"
                        danger
                        onClick={handleUnmapClick}
                        disabled={mapButtonIsDisabled}
                        style={{ width: '100%', marginTop: '1vh' }}
                        size="medium"
                      >
                        {t('unmap_button', 'Unmap Selected')}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal
        title={t('add_new_client_material_title', 'Add New Internal Material')}
        open={isAddModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={addMaterialMutation.isLoading}
        okText={t('add_button')}
        cancelText={t('cancel_button')}
      >
        <AutoComplete
          options={autocompleteOptions}
          style={{ width: '100%' }}
          onSelect={onAutocompleteSelect}
          onSearch={handleAutocompleteSearch}
          placeholder={t('enter_material_name_placeholder')}
          value={newMaterialName}
          onChange={(value) => setNewMaterialName(value)}
          filterOption={false}
          showSearch={true}
          allowClear={true}
        />
      </Modal>
    </div>
  )
}

export default MaterialMapper
